// Keyboard shortcuts. A shortcut is only ever a second way to press a button
// that is on screen right now (SPEC section 15: no key may do what a single
// tap cannot), so each one lives on its button as aria-keyshortcuts, and a
// key press clicks the visible, enabled button that carries it.

/** Gives a button a shortcut: announced to screen readers and shown in its tooltip. */
export function bindKey<T extends HTMLElement>(el: T, key: string): T {
  el.setAttribute('aria-keyshortcuts', key);
  const base = el.title ? `${el.title} · ` : '';
  el.title = `${base}Shortcut: ${key}`;
  return el;
}

/** The name a key goes by in aria-keyshortcuts: letters upper case, the space bar as Space. */
export function keyName(key: string): string {
  if (key === ' ') return 'Space';
  return key.length === 1 ? key.toUpperCase() : key;
}

function visible(el: HTMLElement): boolean {
  return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
}

/** Clicks the on-screen button bound to this key. Returns whether there was one. */
export function pressShortcut(key: string): boolean {
  const name = keyName(key);
  for (const el of document.querySelectorAll<HTMLButtonElement>('button[aria-keyshortcuts]')) {
    if (el.disabled || !el.getAttribute('aria-keyshortcuts')!.split(' ').includes(name) || !visible(el)) continue;
    el.click();
    return true;
  }
  return false;
}

/** Whether a key press should be left alone: typing in a field, a dialog open, or a browser or system shortcut. */
export function ignoreKey(e: KeyboardEvent): boolean {
  return e.ctrlKey || e.metaKey || e.altKey || typingOrDialog(e);
}

/** Whether a key press belongs to a text field or an open dialog, whatever modifiers it has. */
export function typingOrDialog(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  // a slider is not a place to type: a key pressed with it focused still presses its button
  if (t && t.tagName === 'INPUT' && (t as HTMLInputElement).type === 'range') return document.querySelector('.dialog, #join, #feedback') !== null;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return true;
  return document.querySelector('.dialog, #join, #feedback') !== null;
}
